import React from 'react';
import PropTypes from 'prop-types';
import Overlay from '../overlay';
import BalloonInner from './inner';
import { edgeMap as alignMap } from './alignMap';
import getDisabledCompatibleTrigger from './util';

const { Popup } = Overlay;

/** Balloon.Tooltip */
export default class Tooltip extends React.Component {
  static propTypes = {
    /**
     * 样式类名的品牌前缀
     */
    prefix: PropTypes.string,
    /**
     * 自定义类名
     */
    className: PropTypes.string,
    /**
     * 自定义内联样式
     */
    style: PropTypes.object,
    /**
     * 类型
     */
    type: PropTypes.oneOf(['normal', 'primary']),
    /**
     * tooltip的内容
     */
    children: PropTypes.any,
    /**
     * 弹出层位置
     * @enumdesc 上, 右, 下, 左, 上左, 上右, 下左, 下右, 左上, 左下, 右上, 右下 及其 两两组合
     */
    align: PropTypes.oneOf(['t', 'r', 'b', 'l', 'tl', 'tr', 'bl', 'br', 'lt', 'lb', 'rt', 'rb']),
    /**
     * 触发元素
     */
    trigger: PropTypes.any,
    /**
     * 触发行为
     * 鼠标悬浮,  鼠标点击('hover', 'click')或者它们组成的数组，如 ['hover', 'click'], 强烈不建议使用'focus'，若有复杂交互，推荐使用triggerType为click的Balloon组件
     */
    triggerType: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    /**
     * 弹层组件style，透传给Popup
     */
    popupStyle: PropTypes.object,
    /**
     * 弹层组件className，透传给Popup
     */
    popupClassName: PropTypes.string,
    /**
     * 弹层组件属性，透传给Popup
     */
    popupProps: PropTypes.object,
    /**
     * 是否pure render
     */
    pure: PropTypes.bool,
    /**
     * 指定浮层渲染的父节点, 可以为节点id的字符串，也可以返回节点的函数。
     */
    popupContainer: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * 是否跟随滚动
     */
    followTrigger: PropTypes.bool,
    /**
     * 弹层id, 传入值才会支持无障碍
     */
    id: PropTypes.string,
  };

  static defaultProps = {
    type: 'primary',
    triggerType: 'hover',
    prefix: 'next-',
    align: 'b',
    trigger: <span />,
  };

  constructor(props) {
    super(props);
    const expectedAlign = alignMap[props.align][props.rlt ? 'rtlAlign' : 'align'];
    this.state = {
      expectedAlign,
    };
  }

  // 通过overylay的align可选值、alignMap、dir获取toolotip align的简写值
  getSimpleAlign = (expectedAlign, rtl) => {
    const simpleKeys = Object.keys(alignMap);
    if (rtl) {
      return simpleKeys.find(k => alignMap[k].rtlAlign === expectedAlign);
    }
    return simpleKeys.find(k => alignMap[k].align === expectedAlign);
  }

  onPosition = (...args) => {
    if (this.state.expectedAlign !== args[0].align.join(' ')) {
      const expectedAlign = args[0].align.join(' ');
      this.setState({
        expectedAlign,
      });
      this.props.onPosition && this.props.onPosition(...args);
    }
  }

  render() {
    const {
      id,
      type,
      className,
      align,
      style,
      prefix,
      trigger,
      children,
      popupContainer,
      popupProps,
      popupClassName,
      popupStyle,
      followTrigger,
      triggerType,
      autoFocus,
      rtl,
      ...others
    } = this.props;

    let trOrigin = 'trOrigin';
    if (rtl) {
      others.rtl = true;
      trOrigin = 'rtlTrOrigin';
    }
    // 开启对齐方式的自适应调整
    const simpleAlign = this.getSimpleAlign(this.state.expectedAlign, rtl);
    const transformOrigin = alignMap[simpleAlign][trOrigin];
    const _offset = alignMap[simpleAlign].offset;
    const _style = { transformOrigin, ...style };
    const content = (
      <BalloonInner
        {...others}
        id={id}
        prefix={prefix}
        closable={false}
        type={type}
        isTooltip
        className={className}
        style={_style}
        align={simpleAlign}
        rtl={rtl}
      >
        <span className={`${prefix}balloon-tooltip-content`}>{children}</span>
      </BalloonInner>
    );

    const triggerProps = {};
    triggerProps['aria-describedby'] = id;
    triggerProps.tabIndex = '0';

    let newTriggerType = triggerType;

    if (triggerType === 'hover' && id) {
      newTriggerType = ['focus', 'hover'];
    }

    const ariaTrigger = id ? React.cloneElement(trigger, triggerProps) : trigger;

    const newTrigger = getDisabledCompatibleTrigger(
      React.isValidElement(ariaTrigger) ? ariaTrigger : <span>{ariaTrigger}</span>,
    );

    // 不能采用有transform: scale相关的动画，因node.offsetWidth获取的值不精确，
    // 是四舍五入的，而node.getBoundingClientRect()获取的值虽然精确，但是值是视觉大小的
    // 受scale影响
    return (
      <Popup
        role="tooltip"
        container={popupContainer}
        followTrigger={followTrigger}
        trigger={newTrigger}
        triggerType={newTriggerType}
        align={this.state.expectedAlign}
        offset={_offset}
        delay={300}
        className={popupClassName}
        style={popupStyle}
        rtl={rtl}
        autoFocus={triggerType === 'focus' ? false : autoFocus}
        shouldUpdatePosition
        // needAdjust
        {...popupProps}
        onPosition={this.onPosition}
        animation={{
          in: 'fadeIn',
          out: 'fadeOut',
        }}
      >
        {content}
      </Popup>
    );
  }
}
